/**
 *
 * This component is used to display the aggregation type of a calculation.
 * It is used in the CalculationAggregation component.
 */

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux';
import { fontSize, palette } from '../../..';
import ToggleButton from '../../Buttons/ToggleButton';
import MenuIcon from '@mui/icons-material/Menu';
import FunctionsIcon from '@mui/icons-material/Functions';
import { useDispatch } from 'react-redux';
import { setIsAggregated } from '../../../redux/Calculations/CalculationsActions';
import { NotificationDialog } from '../../CommonFunctions/DialogComponents';
import { AlertColor, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
interface ICalculationAggregationProps {
  activeFlowName: string;
  slectedLabel: 'row' | 'visual';
  selectedAggregation: 'Visual level aggregation' | 'Row level calculation';
  propKey: string;
}
function CalculationAggregation({
  activeFlowName,
  slectedLabel,
  selectedAggregation,
  propKey,
}: ICalculationAggregationProps) {
  const { calculations } = useSelector((state: RootState) => ({
    calculations: state.calculations,
  }));
  const [alert, setAlert] = useState<{
    severity: AlertColor;
    message: string;
  } | null>(null);
  const dispatch = useDispatch();
  const currentCalculationSession = calculations.properties[propKey].currentCalculationSession;
  if (!currentCalculationSession) {
    return null;
  }
  /**
   *
   * @param option 'row' | 'visual'
   * @returns void
   * @description This function is used to handle the aggregation switch between row and visual level calculation.
   *  - IF its a @saved Calculation we  can not modify its aggregation
   *  - IF the current flow  is used in any other flows we can not modify  aggregation  of  current flow and its dependent flows
   *  - IF there are more than one conditions in the current flow we can not modify its aggregation
   *  -
   */
  const handleAggregationSwitch = (option: 'row' | 'visual') => {
    // we can change aggregation but the end aggregation will be same that check is done in TestOrCloseFormula.tsx
    // if (currentCalculationSession.uuid) {
    //   setAlert({
    //     severity: 'warning',
    //     message: 'Can not change aggregation for an already saved calculation',
    //   });
    //   return;
    // }

    const activeFlow = currentCalculationSession.activeFlow;
    if (!activeFlow) {
      setAlert({
        severity: 'warning',
        message: 'Select a flow to change aggregation',
      });
      return;
    }

    // const conditionIdsForIfElseOfCurrentCalculation: string[] = [];
    for (const condition of currentCalculationSession.calculationInfo.flows[activeFlow]) {
      if (condition.sourceType.includes('flow')) {
        setAlert({
          severity: 'warning',
          message: 'Can not change aggregation if another flow is used as source',
        });
        return;
      }
      /**
       * If else will be of row level  always
       */
      //if A condition has  a filter that means its if else
      // if (condition.filter) {
      //   conditionIdsForIfElseOfCurrentCalculation.push(condition.filter);
      // }
    }

    // if (conditionIdsForIfElseOfCurrentCalculation.length > 0) {
    //   if (conditionIdsForIfElseOfCurrentCalculation.length > 1) {
    //     setAlert({
    //       severity: 'warning',
    //       message: 'Can not change aggregation if there are more than one conditions.',
    //     });
    //     return;
    //   }

    //   for (const filterID of conditionIdsForIfElseOfCurrentCalculation) {
    //     const filters =
    //       currentCalculationSession.calculationInfo.conditionFilters[filterID][0].conditions;
    //     if (filters.length > 1) {
    //       for (const filter of filters) {
    //         if (filter.leftOperandType[0] === 'flow') {
    //           setAlert({
    //             severity: 'warning',
    //             message: 'Can not change aggregation if another flow is used as source.',
    //           });
    //           return;
    //         } else if (filter.rightOperandType?.includes('flow')) {
    //           setAlert({
    //             severity: 'warning',
    //             message: 'Can not change aggregation if another flow is used as source.',
    //           });
    //           return;
    //         }
    //       }
    //     }
    //   }
    // }

    const allFlows = Object.keys(currentCalculationSession.calculationInfo.flows);
    const flows = currentCalculationSession.calculationInfo.flows;
    const conditionFilters = currentCalculationSession.calculationInfo.conditionFilters;

    for (const flow of allFlows) {
      if (activeFlow === flow) continue;
      for (const step of flows[flow]) {
        if (step.source.includes(activeFlow)) {
          setAlert({
            severity: 'warning',
            message: 'Can not change aggregation for a flow which is used in another flow',
          });
          return;
        }

        if (step.filter) {
          const conditionId = step.filter;
          const allConditions = conditionFilters[conditionId][0].conditions;
          for (const condition of allConditions) {
            if (
              condition.leftOperand[0] === activeFlow ||
              condition.rightOperand?.includes(activeFlow)
            ) {
              setAlert({
                severity: 'warning',
                message: 'Can not change aggregation for a flow which is used in another flow',
              });
              return;
            }
          }
        }
      }
    }

    /**
     *
     *
     *
     * currentCalculationSession.activeFlow === 'ifElse'  -> this condion will alwasy retuns false as activeFlow holds flowId not fkllow name
     *
     *
     *
     */
    const activeCondition =
      currentCalculationSession.activeFlow === 'ifElse'
        ? currentCalculationSession.activeCondition
        : null;
    /**
     * if flow name is concat setIsAggregated will be dispatched twice
     * but visual level is disabled for concat thats why its working fine will be fixeed later
     * 08/08/2025
     */
    if (activeFlowName === 'concat') {
      dispatch(
        setIsAggregated(
          propKey,
          activeCondition,
          currentCalculationSession.activeFlow!,
          option === 'row',
        ),
      );
    }
    dispatch(
      setIsAggregated(
        propKey,
        activeCondition,
        currentCalculationSession.activeFlow!,
        option === 'visual',
      ),
    );
  };

  return (
    <React.Fragment>
      {alert && (
        <NotificationDialog
          severity={alert.severity}
          openAlert={!!alert}
          onCloseAlert={() => setAlert(null)}
          testMessage={alert.message}
        />
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          paddingTop: '0.5rem',
        }}
      >
        <ToggleButton
          button1Label={
            <MenuIcon
              fontSize="small"
              sx={{
                color: palette.primary.contrastText,
              }}
            />
          }
          button2Label={
            <FunctionsIcon
              fontSize="small"
              sx={{
                color: palette.primary.contrastText,
              }}
            />
          }
          onSelect={handleAggregationSwitch}
          disableBtn1={['minDate', 'maxDate', 'absolute', 'log', 'concat', 'summarize'].includes(
            activeFlowName,
          )}
          disableBtn2={[
            'stringToDate',
            'addDateInterval',
            'dateInterval',
            'datePartName',
            'datePartNumber',
            'truncateDate',
            'currentDate',
            'currentTimestamp',
            'IfElse',
            'lowercase',
            'uppercase',
            'propercase',
            'trim',
            'substringleft',
            'substringright',
            'length',
            'ltrim',
            'rtrim',
            'replace',
            'split',
            'min',
            'max',
            'absolute',
            'log',
            'concat',
            'typeCast',
          ].includes(activeFlowName)}
          button1LabelTxt="row"
          button2labelTxt="visual"
          selectedLabel={slectedLabel}
          tooltip1="Click to select Row Level Calculation"
          tooltip2="Click to select Visual Level Aggregation"
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {['log', 'absolute'].includes(activeFlowName) && (
          <Tooltip
            placement="left-start"
            title="<Row Level / Visual level aggregation> is automatically set in this step based on the input."
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <InfoIcon
              fontSize="small"
              sx={{
                color: ' #d3d3d3',
                marginTop: '-9px',
                marginRight: '5px',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        )}
        {['concat'].includes(activeFlowName) && (
          <Tooltip
            placement="left-start"
            title="<Row Level / Visual level aggregation> is automatically set in this step based on the input."
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: '12px',
                },
              },
            }}
          >
            <InfoIcon
              fontSize="small"
              sx={{
                color: ' #d3d3d3',
                marginTop: '-9px',
                marginRight: '5px',
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        )}
        <p
          style={{
            fontSize: fontSize.small,
            color: palette.primary.contrastText,
            marginTop: '3px',
          }}
        >
          {selectedAggregation}
        </p>
      </div>
    </React.Fragment>
  );
}

export default CalculationAggregation;
